import { useState, useRef } from 'react'
import { Link, useLocation, useNavigate, Outlet } from 'react-router'
import { Home, List, BarChart3, Settings, Plus, Camera, Image, Loader2 } from 'lucide-react'
import { NotificationBell } from '@/components/notification/notification-bell'
import { useNotificationSubscription } from '@/lib/hooks'
import { useAuth } from '@/lib/auth-context'
import { useProfiles } from '@/lib/hooks'
import { RecurringProcessor } from '@/components/recurring/recurring-processor'
import { PullToRefresh } from '@/components/pull-to-refresh'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'

const leftTabs = [
  { to: '/', label: 'ホーム', icon: Home },
  { to: '/transactions', label: '一覧', icon: List },
]

const rightTabs = [
  { to: '/report', label: 'レポート', icon: BarChart3 },
  { to: '/settings', label: '設定', icon: Settings },
]

export default function MainLayout() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { user, loading } = useAuth()
  const { data: profiles = [] } = useProfiles()
  const [sheetOpen, setSheetOpen] = useState(false)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      navigate('/receipt', { state: { file } })
    }
    // Reset so the same file can be re-selected
    e.target.value = ''
  }

  useNotificationSubscription()

  const currentProfile = profiles.find((p) => p.id === user?.id)
  const avatarUrl = currentProfile?.avatar_url ?? null

  const isActive = (to: string) => {
    if (to === '/') return pathname === '/'
    return pathname.startsWith(to)
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const renderTab = (tab: { to: string; label: string; icon: React.ComponentType<{ className?: string }> }) => {
    const active = isActive(tab.to)
    const isSettings = tab.to === '/settings'
    return (
      <Link
        key={tab.to}
        to={tab.to}
        replace
        className={`flex flex-1 flex-col items-center gap-1 py-2 text-xs transition-colors ${
          active
            ? 'text-primary'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        {isSettings && avatarUrl ? (
          <img
            src={avatarUrl}
            alt="avatar"
            className={`size-5 rounded-full object-cover ${
              active ? 'ring-2 ring-primary' : ''
            }`}
          />
        ) : (
          <tab.icon className="h-5 w-5" />
        )}
        <span>{tab.label}</span>
      </Link>
    )
  }

  return (
    <div className="bg-background">
      <RecurringProcessor />
      <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-12 max-w-lg items-center justify-between px-4">
          <div className="flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-foreground">
              <circle cx="9.5" cy="12" r="7"/>
              <circle cx="14.5" cy="12" r="7"/>
              <path d="M10 10l2 2 2-2M9.5 13h5M12 12v4.5" strokeWidth="1.5"/>
            </svg>
            <span className="text-sm font-bold" style={{ fontFamily: "'Space Mono', monospace" }}>kakeibo</span>
          </div>
          <NotificationBell />
        </div>
      </header>
      <PullToRefresh>
        <main className="mx-auto max-w-lg pb-[calc(5rem+env(safe-area-inset-bottom))]">
          <Outlet />
        </main>
      </PullToRefresh>
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto flex max-w-lg items-center justify-around">
          {leftTabs.map(renderTab)}

          {/* Central FAB */}
          <div className="flex flex-1 items-center justify-center py-1">
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="flex size-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform active:scale-95"
            >
              <Plus className="size-6" />
            </button>
          </div>

          {rightTabs.map(renderTab)}
        </div>
      </nav>

      {/* Bottom Sheet for add transaction */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl" onOpenAutoFocus={(e) => e.preventDefault()}>
          <SheetHeader>
            <SheetTitle>取引を追加</SheetTitle>
            <SheetDescription>記録方法を選んでください</SheetDescription>
          </SheetHeader>
          <div className="flex gap-3 px-4">
            <button
              type="button"
              className="flex flex-1 flex-col items-center gap-3 rounded-xl border p-4 transition-colors hover:bg-muted"
              onClick={() => {
                setSheetOpen(false)
                cameraInputRef.current?.click()
              }}
            >
              <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Camera className="size-6" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium">レシートを撮影</p>
                <p className="text-xs text-muted-foreground">AIがレシートを解析します</p>
              </div>
            </button>
            <button
              type="button"
              className="flex flex-1 flex-col items-center gap-3 rounded-xl border p-4 transition-colors hover:bg-muted"
              onClick={() => {
                setSheetOpen(false)
                photoInputRef.current?.click()
              }}
            >
              <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Image className="size-6" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium">写真から入力</p>
                <p className="text-xs text-muted-foreground">AIが写真を解析します</p>
              </div>
            </button>
          </div>
          <div className="px-4 pb-8 pt-2 text-center">
            <button
              type="button"
              className="text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
              onClick={() => {
                setSheetOpen(false)
                navigate('/transactions/new')
              }}
            >
              手動で入力する
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Hidden file inputs — must live here so click() is within user gesture */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelected}
      />
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelected}
      />
    </div>
  )
}
